/**
 * Whether a learner may state another objective, and what to tell them when they may not.
 *
 * **The screen has to know this, and until 2026-09-19 nothing did.** The create form rendered
 * only for a learner with no objective at all, which was right while the slot cap was one and
 * became the reason a second plan could not be created on the day the cap went to three: the API
 * would have accepted it, and no screen offered it. The owner found it by looking for the tabs
 * and finding no way to earn them.
 *
 * Pure, and here rather than in the component, because the interesting part is what happens when
 * the answer is not known — and a branch nothing can test is how the current bug survived.
 */

/** What the plan screen needs to decide, which is less than an entitlement. */
export interface Room {
  /** Live plans right now. `undefined` while they are still being read. */
  live: number | undefined;
  /** Slots the tier grants, from `/api/auth/me`. `undefined` when that read failed. */
  cap: number | undefined;
}

export type Offer =
  /** Render the form. */
  | { offer: true; remaining: number | undefined }
  /** They are at the cap: say so, and say what to do about it. */
  | { offer: false; reason: 'at_cap'; cap: number }
  /** Still loading. Render nothing rather than flicker a form away. */
  | { offer: false; reason: 'unknown' };

/**
 * **Degrades towards offering, and that direction is the decision.**
 *
 * When the cap cannot be read — `/api/auth/me` failed, an older browser, a field that is not
 * there yet — the form is shown. The two ways to be wrong are not equal: offering a form to
 * somebody at their cap costs them one refusal with copy that explains it, and *hiding* it costs
 * them a feature they are paying for with no way to discover it. The second is the bug this file
 * exists to close, and a cautious default would have reproduced it exactly.
 *
 * `live === undefined` is the one case that shows nothing, because it means the plans have not
 * arrived yet — and a form that appears and then vanishes is worse than one that arrives a beat
 * late.
 */
export function roomForAnother({ live, cap }: Room): Offer {
  if (live === undefined) return { offer: false, reason: 'unknown' };
  if (cap === undefined) return { offer: true, remaining: undefined };
  if (live >= cap) return { offer: false, reason: 'at_cap', cap };
  return { offer: true, remaining: cap - live };
}

/**
 * What the clean slate shows, which is five things and only one of them is the form. F-163.
 *
 * **It is here rather than in `Objectives.tsx` because every wrong answer is invisible**, which
 * is this module's own argument applied to the screen it feeds. `npm test` does not type-check a
 * `.tsx` file at all, so an ordering decided in JSX is a decision nothing in this repository can
 * assert — and the review of F-163's first draft found two faults in exactly this ordering, both
 * of which rendered something plausible and neither of which threw:
 *
 * - a failed read was treated as a read in flight, so a learner whose one request was dropped got
 *   "Loading…" for ever, on a screen with no selector above it and no plan bar under it;
 * - a learner with no live plan at all was shown the form on a screen with neither of its two
 *   exits, which is a strictly worse `/plan` — that screen renders the same one definition of the
 *   form *and* the history of closed objectives beneath it.
 *
 * **The order is the decision.** `failed` outranks everything, because a spinner over a read
 * nobody is making is the failure above. `undefined` live plans is genuinely in flight and says
 * so. Nought live plans is not this screen's business at all. Only then does the cap decide
 * between the refusal and the form.
 */
export type CleanSlate =
  /** The shared read was tried and lost. Say so, and offer the way to try again. */
  | { show: 'failed' }
  /** Nobody has answered yet. A moment, honestly described. */
  | { show: 'loading' }
  /** No live plan: `/plan` is this learner's clean slate already, and a better one. */
  | { show: 'no_plan' }
  /** Every slot is taken. The refusal, and the way back to a plan. */
  | { show: 'at_cap'; cap: number }
  /** The form, and the sentence saying how many more plans are allowed. */
  | { show: 'form'; remaining: number | undefined };

/**
 * Which of those five, given what is actually known.
 *
 * `failed` is the provider's "tried and lost" and not the absence of a list — see
 * `plan/Plans.tsx`. The two used to be one state, which is what produced the endless spinner.
 *
 * Built on `roomForAnother` rather than beside it, so there is one answer to "is there room" in
 * the browser and the selector's *add a plan* cannot disagree with the screen it leads to.
 */
export function cleanSlate(input: Room & { failed: boolean }): CleanSlate {
  if (input.failed) return { show: 'failed' };
  if (input.live === undefined) return { show: 'loading' };
  if (input.live === 0) return { show: 'no_plan' };
  const room = roomForAnother(input);
  /* `unknown` is unreachable here — `live` is a number by this line, which is the only thing
     `roomForAnother` answers `unknown` for — but it is matched rather than assumed, because a
     cast would be a silent lie the day that function learns a second reason to say so. */
  if (!room.offer) {
    return room.reason === 'at_cap' ? { show: 'at_cap', cap: room.cap } : { show: 'loading' };
  }
  return { show: 'form', remaining: room.remaining };
}
